import i18n from 'config/i18n';
import { COLOR_OPTIONS } from 'config/style';
import convert from 'convert-units';
import { UnitSystem, getCurrentUnitSystem, parseNumber } from 'core/utils/functions';
import type { FeatureCollection, Point } from 'geojson';
import { groupBy, isEmpty, sumBy } from 'lodash';
import moment from 'moment';
import type { RegionalOverviewProperties } from 'querys/regional-overview/regional-overview.query.models';
import { RegionalRiskMycotoxinHandlerStatistics, VariableTypes } from './regional-overview-mycotoxin.models';

export const optionsStatistics = (currentUnitSystem: string): Highcharts.Options => {
  const typeUnitSystem = getTypeArea(currentUnitSystem);
  const options: Highcharts.Options = {
    chart: {
      type: 'variablepie'
    },
    tooltip: {
      headerFormat: '',
      pointFormat: `<span style="color:#fff">  {point.name} {point.y}% ({point.areaSize} ${typeUnitSystem})  </span>`,
      backgroundColor: '#000',
      borderColor: COLOR_OPTIONS.NEUTRAL_90,
      outside: true
    },
    title: {
      text: ''
    },
    credits: {
      enabled: false
    },
    subtitle: {
      text: ''
    },
    legend: {
      enabled: false
    },
    series: [
      {
        type: 'variablepie',
        minPointSize: 1,
        maxPointSize: 100,
        innerSize: '70%',
        zMin: 0,
        dataLabels: {
          enabled: false
        },
        data: []
      }
    ]
  };
  return options;
};

const getPercentage = (valueDiff: number, valueTotal: number) => {
  const result = (valueDiff / valueTotal) * 100;
  return Math.round(result);
};

export const handleDataStatisticsMycotoxin = (data: FeatureCollection<Point, RegionalOverviewProperties>, currentUnitSystem: string) => {
  const dataMycotoxinGroupByRisk = groupBy(data?.features, 'properties.risk');
  const totalArea = sumBy(data?.features, itemPoint => Number.parseFloat(itemPoint?.properties?.field_size));
  return Object.entries(dataMycotoxinGroupByRisk)
    ?.map(item => {
      const riskKey = item[0] as keyof typeof RegionalRiskMycotoxinHandlerStatistics;
      const totalAreaByRisk = sumBy(item[1], itemData => Number.parseFloat(itemData?.properties?.field_size));
      return {
        name: i18n.t(RegionalRiskMycotoxinHandlerStatistics[riskKey].riskName),
        y: getPercentage(totalAreaByRisk, totalArea),
        z: 20,
        color: i18n.t(RegionalRiskMycotoxinHandlerStatistics[riskKey].riskColor),
        riskIndex: item[0],
        areaSize: formatArea(totalAreaByRisk, currentUnitSystem),
        totalFields: item[1].length
      };
    })
    .reverse();
};

export const formatArea = (value: number | string, CurrentUnitRedux: string, toFixedValue = 0): string => {
  const unitSystem = getCurrentUnitSystem();
  let areaValue = parseNumber(value);
  const currentUnitEqual = CurrentUnitRedux === unitSystem;
  if (currentUnitEqual && CurrentUnitRedux === UnitSystem.IMPERIAL) {
    areaValue = convert(areaValue).from('ha').to('ac');
  }

  return areaValue.toFixed(toFixedValue);
};

export const getTypeArea = (CurrentUnitRedux: string): string => {
  const unitSystem = getCurrentUnitSystem();
  let typeUnitArea = 'ha';
  const currentUnitEqual = CurrentUnitRedux === unitSystem;
  if (currentUnitEqual && CurrentUnitRedux === UnitSystem.IMPERIAL) {
    typeUnitArea = 'ac';
  }
  return typeUnitArea;
};

export const setCorrectValues = (type: VariableTypes, variable: string | number | Date | undefined, currentUnitSystem?: string) => {
  let valueToReturn: string | number | Date = '-';

  if (!variable || isEmpty(variable)) return valueToReturn;

  switch (type) {
    case VariableTypes.AGRONOMIC_CLASS:
    case VariableTypes.RISK:
    case VariableTypes.PREVIOUSLY_CROP:
    case VariableTypes.TILLAGE:
    case VariableTypes.VARIETY:
    case VariableTypes.PROPERTY_NAME: {
      valueToReturn = variable;
      break;
    }
    case VariableTypes.BLOOMING_DATE:
    case VariableTypes.PLANTING_DATE: {
      valueToReturn = moment(variable).format('ll');
      break;
    }
    case VariableTypes.FIELD_SIZE: {
      if (currentUnitSystem) {
        valueToReturn = `${formatArea(variable.toString(), currentUnitSystem, 2)} ${getTypeArea(currentUnitSystem)}`;
      }
      break;
    }
    case VariableTypes.RISK_VALUE: {
      valueToReturn = Math.round((+variable * 100 + Number.EPSILON) * 10) / 10;
      break;
    }
    default:
      valueToReturn = '';
      break;
  }

  if (typeof valueToReturn !== 'string') {
    valueToReturn = valueToReturn.toString();
  }

  return valueToReturn;
};
